import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Button, Table, Modal, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  fetchGroupWatermarks,
  updateOrNewWatermark,
  deleteWatermark
} from '../../../actions/watermark'
import CONFIG from '../../../constants/config'
import sensors from '../../../utils/sensors'
import RenameButton from './components/RenameButton'
import CopyButton from './components/CopyButton'
import WatermarkCover from './components/WatermarkCover'
import DepartmentUserPicker, {
  FilterObj
} from '../../components/DepartmentMemberPicker'
import * as WM from '../typings'
import { capturePromiseException } from '../../../utils/sentryUtils'
import crown_golden from '../../../images/crown_golden.png'
import { EDITABLE_BASE_IDS } from '../../../constants/watermark'
import { QuestionCircleOutlined } from '@ant-design/icons'

const Column = Table.Column

const Published: React.FC<WM.PublishedProps> = ({ keyword }) => {
  const [departmentUserPickerVisible, setDepartmentUserPickerVisible] =
    useState<boolean>(false)
  const [curWatermark, setCurWatermark] = useState<WM.Watermark | null>(null)
  const [filterObj, setFilterObj] = useState<FilterObj>({
    members: [],
    userDepartmentIDs: [],
    departments: []
  })
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const user = useAppSelector((state) => state.main.user)
  const groupID = useAppSelector((state) => state.group.groupId)
  useEffect(() => {
    fetchWatermarks()
  }, [])
  const fetchWatermarks = useCallback(() => {
    dispatch(
      fetchGroupWatermarks({
        userID: user?.user_id,
        groupID: groupID
      })
    )
  }, [user, groupID])

  const watermarkList = useAppSelector((state) => state.watermark.watermarkList)
  const dataSource: WM.Watermark[] = useMemo(() => {
    return watermarkList
      .map((watermark: WM.OriginWatermark) => {
        let watermarkContent = null
        try {
          watermarkContent = JSON.parse(watermark.content)
        } catch (err) {
          console.log('水印信息解析失败，数据错误，水印ID：' + watermark.id)
        }
        return {
          ...watermark,
          watermarkContent
        }
      })
      .filter((watermark: WM.Watermark) => {
        if (keyword.trim()) {
          return (
            watermark.watermarkContent &&
            watermark.watermarkContent.name?.includes(keyword.trim())
          )
        } else {
          return true
        }
      })
  }, [watermarkList, keyword])

  const onEdit = useCallback(
    (watermarkID) => {
      sensors.track('web_workgroup_watermark_manage_page_click', {
        clickItem: 'edit'
      })
      navigate('/watermark/editor?action=modify&watermark_id=' + watermarkID)
    },
    [navigate]
  )

  const onDeleteClick = useCallback((watermark) => {
    Modal.confirm({
      title: '确认要删除这个模板吗？',
      onOk: () => {
        dispatch(
          deleteWatermark({
            userID: user?.user_id,
            groupID: groupID,
            watermarkID: watermark.id
          })
        )
          .then(fetchWatermarks)
          .catch(capturePromiseException)
      }
    })
  }, [])

  const onGrantClick = useCallback((watermark: WM.Watermark) => {
    setDepartmentUserPickerVisible(true)
    setCurWatermark(watermark)
    setFilterObj({
      members: [],
      userDepartmentIDs: watermark.grant.departmentUserIDs,
      departments: watermark.grant.departments
    })
  }, [])

  const onGrant = useCallback(
    (params) => {
      const {
        selectedNormalUserIDs,
        selectedDepartmentUserIDs,
        selectedDepartments
      } = params
      const usedItems =
        curWatermark?.watermarkContent?.items
          ?.filter((item) => item.switchStatus)
          ?.map((item) => item.id) || []
      if (curWatermark?.watermarkContent?.logo?.switchStatus) {
        usedItems.push(curWatermark.watermarkContent.logo.id)
      }
      if (curWatermark?.watermarkContent?.theme?.switchStatus) {
        usedItems.push(curWatermark.watermarkContent.theme.id)
      }
      dispatch(
        updateOrNewWatermark({
          userID: user?.user_id,
          groupID,
          watermark: {
            ...curWatermark,
            grant: {
              departmentIDs: selectedDepartments.map(
                (department) => department.id
              ),
              departmentUserIDs: selectedDepartmentUserIDs,
              userIDs: selectedNormalUserIDs
            }
          },
          insInfo: {
            operateTemplateID: '',
            fromPlace: 'webWatermarkManage',
            isAutoCreateGroup: 0
          },
          usedItems: usedItems
        })
      )
        .then(() => {
          setDepartmentUserPickerVisible(false)
          fetchWatermarks()
        })
        .catch(capturePromiseException)
    },
    [curWatermark]
  )

  return (
    <div>
      <Table dataSource={dataSource} rowKey="id" pagination={false}>
        <Column
          width="20%"
          align="center"
          title="封面图"
          key="cover"
          render={(record: WM.Watermark) => (
            <WatermarkCover watermark={record} />
          )}
        />
        <Column
          width="20%"
          title="模板名称"
          dataIndex={['watermarkContent', 'name']}
        />
        <Column
          width="20%"
          title="创建时间"
          dataIndex="createTime"
          render={(text: string) =>
            dayjs(parseInt(text)).format(CONFIG.photoTimeFormat)
          }
        />
        <Column
          width="20%"
          ellipsis
          title={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <img
                style={{ width: '20px', marginRight: '5px' }}
                src={crown_golden}
                alt=""
              />
              使用范围
            </span>
          }
          dataIndex={['grant', 'tips']}
          render={(text, record: WM.Watermark) => (
            <a href="#" onClick={() => onGrantClick(record)}>
              {text || '全部成员'}
            </a>
          )}
        />
        <Column
          width={180}
          title="操作"
          render={(record: WM.Watermark) => {
            const baseId = record.watermarkContent?.base_id
            if (!baseId || !EDITABLE_BASE_IDS.includes(baseId)) {
              // if (record.watermarkContent.base_id === '34') {
              return (
                <Tooltip title="此类水印暂不支持编辑，请使用手机客户端进行编辑。">
                  <span>
                    <QuestionCircleOutlined />
                  </span>
                </Tooltip>
              )
            } else {
              return [
                <Button
                  key="edit"
                  type="link"
                  disabled={!record.watermarkContent}
                  onClick={() => onEdit(record.id)}
                >
                  编辑
                </Button>,
                <RenameButton key="rename" watermark={record} />,
                <CopyButton key="copy" watermark={record} />,
                <Button
                  key="copy-id"
                  type="link"
                  onClick={() => {
                    navigator.clipboard.writeText(record.id).then(function () {
                      Modal.success({
                        title: '复制成功'
                      })
                    })
                  }}
                >
                  复制ID
                </Button>,
                <Button
                  style={{ color: '#EB524A' }}
                  key="delete"
                  type="link"
                  onClick={() => onDeleteClick(record)}
                >
                  删除
                </Button>
              ]
            }
          }}
        />
      </Table>
      <DepartmentUserPicker
        title="设置水印使用范围"
        open={departmentUserPickerVisible}
        onlyInGroup
        vipRequired
        // limitedTimeFree // 3.0.50版本后，不再支持限时免费
        onCancel={() => setDepartmentUserPickerVisible(false)}
        onOk={onGrant}
        filterObj={filterObj}
      />
    </div>
  )
}

export default Published
